const FULL_CIRCLE = 360;
const HERO_BOTTOM_ANGLE_DEG = 90;

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Адаптивный пресет места: масштаб карточки и степень "свёрнутости" метаданных.
 */
export function getSeatDensityPreset(playersCount) {
  if (playersCount >= 8) {
    return {
      seatScale: 0.74,
      compact: true,
      hideSecondaryMeta: true,
      hideLastActionBadges: true,
    };
  }
  if (playersCount >= 6) {
    return {
      seatScale: 0.84,
      compact: true,
      hideSecondaryMeta: false,
      hideLastActionBadges: false,
    };
  }
  if (playersCount >= 5) {
    return {
      seatScale: 0.9,
      compact: true,
      hideSecondaryMeta: false,
      hideLastActionBadges: false,
    };
  }
  return {
    seatScale: 1,
    compact: true,
    hideSecondaryMeta: false,
    hideLastActionBadges: false,
  };
}

/**
 * Кольцевая/эллиптическая рассадка вокруг стола.
 * Возвращает стили в % для absolute-позиционирования.
 */
export function buildEllipticSeatSlots({
  seats,
  heroSeat = 0,
  centerX = 50,
  centerY = 51,
  radiusX = 44,
  radiusY = 34,
} = {}) {
  const orderedSeats = Array.isArray(seats) ? [...seats] : [];
  if (!orderedSeats.length) return [];

  const pivotIndex = orderedSeats.indexOf(heroSeat);
  const rotatedSeats =
    pivotIndex >= 0
      ? [...orderedSeats.slice(pivotIndex), ...orderedSeats.slice(0, pivotIndex)]
      : orderedSeats;
  const step = FULL_CIRCLE / orderedSeats.length;

  return rotatedSeats.map((seat, ringIndex) => {
    const angleDeg = HERO_BOTTOM_ANGLE_DEG + ringIndex * step;
    const angle = toRad(angleDeg);
    const left = centerX + radiusX * Math.cos(angle);
    const top = centerY + radiusY * Math.sin(angle);
    return {
      seat,
      angleDeg,
      leftPct: left,
      topPct: top,
      isHero: seat === heroSeat,
    };
  });
}

/**
 * Тюнинг safe-zones и размеров для разных плотностей стола.
 * @param {number} playersCount
 * @param {'default' | 'igra'} [variant] — `igra`: ниже кольцо мест под перспективу и фото стола.
 */
export function getSeatArenaTuning(playersCount, variant = 'default') {
  const isIgra = variant === 'igra';

  /** @type {{ centerY: number; radiusX: number; radiusY: number; minHeightClass: string; maxWidthClass: string }} */
  let base;
  if (playersCount >= 8) {
    base = {
      centerY: 50,
      radiusX: 45,
      radiusY: 35,
      minHeightClass: 'min-h-[340px] sm:min-h-[360px]',
      maxWidthClass: 'max-w-[min(100%,24rem)] sm:max-w-[min(100%,27rem)]',
    };
  } else if (playersCount >= 6) {
    base = {
      centerY: 51,
      radiusX: 44,
      radiusY: 34,
      minHeightClass: 'min-h-[332px] sm:min-h-[352px]',
      maxWidthClass: 'max-w-[min(100%,24rem)] sm:max-w-[min(100%,27rem)]',
    };
  } else {
    base = {
      centerY: 52,
      radiusX: 42,
      radiusY: 32,
      /** Мобильный портрет: ниже минимум, чтобы стол + рука + торги умещались без лишнего скролла. */
      minHeightClass: 'min-h-[252px] sm:min-h-[300px] md:min-h-[328px]',
      maxWidthClass: 'max-w-[min(100%,23rem)] sm:max-w-[min(100%,26rem)]',
    };
  }

  if (isIgra && playersCount < 6) {
    return {
      ...base,
      /* Эллипс шире к краям портрета; clamp см. `clampSeatBelotIgraEllipse` в seatLayoutOffsets. */
      centerY: 50,
      radiusX: 46,
      radiusY: 50,
      minHeightClass: 'min-h-[276px] sm:min-h-[316px] md:min-h-[346px]',
      maxWidthClass: 'max-w-[min(100%,26rem)] sm:max-w-[min(100%,28rem)]',
    };
  }

  return base;
}

/**
 * Четыре места белота в «столбике» для узкого экрана (герой снизу).
 * @param {{ variant?: 'default' | 'igra' }} [options] — `igra`: опустить ряд под фото стола.
 */
export function buildBelotFourMobileStackSlots(options = {}) {
  const igra = options.variant === 'igra';
  const nudgeY = igra ? 8 : 0;
  const leftSide = igra ? 9 : 14;
  const rightSide = igra ? 91 : 86;
  return [
    { seat: 2, angleDeg: 270, leftPct: 50, topPct: 17 + nudgeY, isHero: false },
    { seat: 1, angleDeg: 235, leftPct: leftSide, topPct: 40 + nudgeY, isHero: false },
    { seat: 3, angleDeg: 305, leftPct: rightSide, topPct: 40 + nudgeY, isHero: false },
    { seat: 0, angleDeg: 90, leftPct: 50, topPct: 84 + Math.min(nudgeY, igra ? 6 : 4), isHero: true },
  ];
}

